/**
 * Cook mode (ROADMAP §3 WS-19) — pure duration parser. Scans a recipe step's free text for
 * cook-relevant durations ("20 minutes", "1 hour", "35-40 min", "35 to 40 minutes", "1½ hrs") and returns
 * tappable-chip candidates. No DOM, no React — just strings in, structured data out, so it
 * is trivially unit-testable and reusable if the AI meal-planner module ever wants the same
 * parsing (WS-11).
 *
 * Deliberately narrow: a number must be immediately (optional whitespace only) followed by
 * a recognized time-unit word. "350 degrees", "400°F", "2 cups" never match because
 * "degrees"/"°F"/"cups" aren't in the unit vocabulary — no allow/deny list of oven-temp
 * phrases needed, the grammar just doesn't produce a false hit.
 */

const FRACTION_VALUES: Record<string, number> = {
  '¼': 1 / 4,
  '½': 1 / 2,
  '¾': 3 / 4,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '⅕': 1 / 5,
  '⅖': 2 / 5,
  '⅗': 3 / 5,
  '⅘': 4 / 5,
  '⅙': 1 / 6,
  '⅚': 5 / 6,
  '⅛': 1 / 8,
  '⅜': 3 / 8,
  '⅝': 5 / 8,
  '⅞': 7 / 8,
};
const FRACTION_CHARS = Object.keys(FRACTION_VALUES).join('');

/** A single number token: glued mixed unicode ("1½"), simple fraction ("1/2"), plain
 * decimal ("35", "1.5"), or a bare unicode fraction ("½"). Order matters for the glued
 * case — it must be tried before the plain-decimal alternative consumes just the "1". */
const NUMBER_TOKEN = `(?:\\d+[${FRACTION_CHARS}]|\\d+\\/\\d+|\\d+(?:\\.\\d+)?|[${FRACTION_CHARS}])`;

const HOUR_UNIT = 'hours?|hrs?|h';
const MINUTE_UNIT = 'minutes?|mins?|m';

const DURATION_RE = new RegExp(
  `(${NUMBER_TOKEN})(?:(?:\\s*[-–]\\s*|\\s+to\\s+)(${NUMBER_TOKEN}))?\\s*(${HOUR_UNIT}|${MINUTE_UNIT})\\b`,
  'gi',
);

const HOUR_WORD_RE = /^h/i;
const GLUED_FRACTION_RE = new RegExp(`^(\\d+)([${FRACTION_CHARS}])$`);
const SIMPLE_FRACTION_RE = /^(\d+)\/(\d+)$/;
const DECIMAL_RE = /^\d+(?:\.\d+)?$/;

function parseNumberToken(token: string): number | null {
  const fractionOnly = FRACTION_VALUES[token];
  if (fractionOnly !== undefined) return fractionOnly;
  const glued = GLUED_FRACTION_RE.exec(token);
  if (glued) {
    const whole = Number(glued[1]);
    const frac = FRACTION_VALUES[glued[2]!];
    if (frac !== undefined) return whole + frac;
  }
  const simpleFraction = SIMPLE_FRACTION_RE.exec(token);
  if (simpleFraction) {
    const denom = Number(simpleFraction[2]);
    if (denom === 0) return null;
    return Number(simpleFraction[1]) / denom;
  }
  if (DECIMAL_RE.test(token)) return Number(token);
  return null;
}

/** Formats a number for a chip label: whole numbers plain, common fractions as a unicode
 * glyph (1.5 -> "1½"), anything else falls back to a trimmed decimal. */
function formatDurationNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  const whole = Math.floor(value);
  const frac = value - whole;
  let bestGlyph: string | null = null;
  let bestDist = Infinity;
  for (const [glyph, glyphValue] of Object.entries(FRACTION_VALUES)) {
    const dist = Math.abs(frac - glyphValue);
    if (dist < bestDist) {
      bestDist = dist;
      bestGlyph = glyph;
    }
  }
  if (bestGlyph !== null && bestDist < 0.02) {
    return whole > 0 ? `${whole}${bestGlyph}` : bestGlyph;
  }
  return String(Math.round(value * 100) / 100);
}

export interface ParsedDuration {
  /** Start/end offsets into the source text, for stable React keys. */
  start: number;
  end: number;
  /** Raw substring matched ("35-40 min", "1½ hrs"). */
  raw: string;
  /** Seconds to actually run the timer for — the LOWER bound of a range. */
  seconds: number;
  /** Chip label — shows the full range ("35–40 min") but the timer itself uses `seconds`. */
  label: string;
}

/**
 * Finds every duration mention in `text`. Multiple durations in one step ("simmer 10
 * minutes, then rest 5 minutes") each get their own entry, left to right.
 */
export function parseDurations(text: string): ParsedDuration[] {
  const results: ParsedDuration[] = [];
  DURATION_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = DURATION_RE.exec(text)) !== null) {
    const [raw, lowToken, highToken, unitToken] = match;
    const low = parseNumberToken(lowToken!);
    if (low === null) continue;
    const high = highToken ? parseNumberToken(highToken) : null;
    const isHour = HOUR_WORD_RE.test(unitToken!);
    const multiplier = isHour ? 3600 : 60;
    const seconds = Math.round(low * multiplier);
    if (seconds <= 0) continue;

    const count = high ?? low;
    const unitWord = isHour ? (count === 1 ? 'hr' : 'hrs') : count === 1 ? 'min' : 'mins';
    const lowLabel = formatDurationNumber(low);
    const label =
      high !== null
        ? `${lowLabel}–${formatDurationNumber(high)} ${unitWord}`
        : `${lowLabel} ${unitWord}`;

    results.push({
      start: match.index,
      end: match.index + raw.length,
      raw,
      seconds,
      label,
    });
  }
  return results;
}
